import React, { createContext, useContext, useEffect, useState } from "react";

interface SidebarContextType {
  isCollapsed: boolean;
  toggle: () => void;
  setCollapsed: (collapsed: boolean) => void;
  isHydrated: boolean;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

interface SidebarProviderProps {
  children: React.ReactNode;
  initialState?: boolean;
}

export function SidebarProvider({
  children,
  initialState = false,
}: SidebarProviderProps) {
  const [isCollapsed, setIsCollapsed] = useState(initialState);
  const [isHydrated, setIsHydrated] = useState(false);

  // Hidratar desde localStorage al montar
  useEffect(() => {
    const saved = localStorage.getItem("sidebar-collapsed");
    if (saved !== null) {
      setIsCollapsed(saved === "true");
    }
    // Delay adicional para asegurar que todo esté completamente montado
    const timer = setTimeout(() => {
      setIsHydrated(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Guardar en localStorage cuando cambie el estado
  useEffect(() => {
    if (isHydrated) {
      localStorage.setItem("sidebar-collapsed", isCollapsed.toString());
    }
  }, [isCollapsed, isHydrated]);

  const toggle = () => {
    setIsCollapsed((prev) => !prev);
  };

  const setCollapsed = (collapsed: boolean) => {
    setIsCollapsed(collapsed);
  };

  // Evitar flash durante hidratación
  if (!isHydrated) {
    return (
      <SidebarContext.Provider
        value={{
          isCollapsed: initialState,
          toggle: () => {},
          setCollapsed: () => {},
          isHydrated: false,
        }}
      >
        {children}
      </SidebarContext.Provider>
    );
  }

  return (
    <SidebarContext.Provider
      value={{
        isCollapsed,
        toggle,
        setCollapsed,
        isHydrated,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (context === undefined) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}
